import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./MultiSelectFilter.module.css";

export default function MultiSelectFilter({
  dropdownTitle,
  placeholder = "Type to search...",
  fetchData,
  selected = [],
  onSelectionChange,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [internalSelected, setInternalSelected] = useState(selected);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [isInputActive, setIsInputActive] = useState(false);

  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const cacheRef = useRef({}); // cache per query

  /* ---------------- Sync external selection ---------------- */
  useEffect(() => {
    setInternalSelected(selected);
  }, [selected]);

  /* ---------------- Outside click close ---------------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setIsInputActive(false);
        setQuery("");
        setSuggestions([]);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------------- Fetch suggestions (cached) ---------------- */
  const fetchSuggestions = useCallback(
    (search) => {
      if (!fetchData) return;

      if (cacheRef.current[search]) {
        setSuggestions(cacheRef.current[search]);
        setLoading(false);
        return;
      }

      setLoading(true);

      fetchData(search)
        .then((data) => {
          cacheRef.current[search] = data;
          setSuggestions(data);
        })
        .catch(() => setSuggestions([]))
        .finally(() => setLoading(false));
    },
    [fetchData]
  );

  /* ---------------- Debounced fetch ONLY on input interaction ---------------- */
  useEffect(() => {
    if (!open || !isInputActive) return;

    clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(query || "");
    }, 500);

    return () => clearTimeout(debounceRef.current);
  }, [query, open, isInputActive, fetchSuggestions]);

  /* ---------------- Helpers ---------------- */
  const getValue = (item) =>
    typeof item === "object" ? item.value || item.label : item;

  const isSelected = (item) =>
    internalSelected.some((s) => getValue(s) === getValue(item));

  const addItem = (item) => {
    if (isSelected(item)) return;

    const updated = [...internalSelected, item];
    setInternalSelected(updated);
    
    // Always extract values for API calls
    const dataToSend = updated.map(item => getValue(item));
    onSelectionChange?.(dataToSend);

    setQuery("");
    setHighlightIndex(-1);
  };

  const removeItem = (item) => {
    const updated = internalSelected.filter(
      (s) => getValue(s) !== getValue(item)
    );
    setInternalSelected(updated);
    
    // Always extract values for API calls
    const dataToSend = updated.map(item => getValue(item));
    onSelectionChange?.(dataToSend);
  };

  /* ---------------- Keyboard navigation ---------------- */
  const handleKeyDown = (e) => {
    if (!suggestions.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, suggestions.length - 1));
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const item = suggestions[highlightIndex];
      if (item && !isSelected(item)) addItem(item);
    }

    if (e.key === "Escape") {
      setOpen(false);
      setIsInputActive(false);
    }
  };

  /* ---------------- Render ---------------- */
  return (
    <div className={styles.filterWrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.filterBtn}
        onClick={() => setOpen((o) => !o)}
      >
        {dropdownTitle}
        {internalSelected.length > 0 && (
          <span className={styles.countBadge}>{internalSelected.length}</span>
        )}
      </button>

      {open && (
        <div className={styles.dropdown}>
          {/* Selected Chips */}
          <div className={styles.selectedChips}>
            {internalSelected.map((s, i) => (
              <span key={getValue(s) + i} className={styles.chip}>
                {s.label || s}
                <button onClick={() => removeItem(s)}>×</button>
              </span>
            ))}
          </div>

          {/* Input */}
          <input
            type="text"
            value={query}
            placeholder={placeholder}
            onFocus={() => {
              setIsInputActive(true);
              setOpen(true);
            }}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsInputActive(true);
            }}
            onKeyDown={handleKeyDown}
            className={styles.input}
            autoFocus
          />

          {/* Loading */}
          {isInputActive && loading && (
            <div className={styles.loading}>Loading...</div>
          )}

          {/* Suggestions */}
          {isInputActive && !loading && suggestions.length > 0 && (
            <ul className={styles.suggestions}>
              {suggestions.map((s, idx) => {
                const disabled = isSelected(s);
                return (
                  <li
                    key={getValue(s)}
                    className={`${disabled ? styles.disabled : ""} ${
                      idx === highlightIndex ? styles.highlight : ""
                    }`}
                    onClick={() => !disabled && addItem(s)}
                  >
                    {s.label || s}
                    {disabled && " ✓"}
                  </li>
                );
              })}
            </ul>
          )}

          {/* No results */}
          {isInputActive && !loading && suggestions.length === 0 && (
            <div className={styles.noResults}>No results found</div>
          )}
        </div>
      )}
    </div>
  );
}
